import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { useButton } from '~Hooks';
import { useI18n } from '~Hooks';
import { BaseButton } from '~Scenes/Handy/styles';
import { handyBreakpoint } from '~Styles/breakpoints';
import { useStorage } from '~Components/Storage';

const Wrapper = styled.div`
  position: relative;
  background-color: white;
  padding: 8px 32px;
  flex: 1;
  border-radius: 3px;
  border: 1px solid var(--colorBlueGray300);
  text-align: left;
  margin-bottom: 4px;
  color: var(--colorBlueGray1200);

  ${handyBreakpoint} {
    padding: 16px;
  }
`;

const InnerContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  ${handyBreakpoint} {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    text-align: left;
  }
`;

const TerminalRowCell = styled.div`
  text-align: left;
  flex: 0 0 auto;

  &:nth-of-type(1) {
    width: 280px; // terminal ID column
  }

  &:nth-of-type(2) {
    width: 220px; // name column
  }

  &:nth-of-type(3) {
    margin-left: auto; // push button to far right
  }
`;

const AddButton = styled(BaseButton)`
  background-color: var(--colorUIGreen);
  width: 100px;
`;

interface Props {
  id: string;
  onAdd: () => void;
}

const AvailableTerminal: React.FunctionComponent<Props> = ({ id, onAdd }) => {
  const { i18n } = useI18n();
  const [data, updateStorage] = useStorage();
  const button = useButton({
    onClick: onAdd,
  });

  const terminalName = useMemo(() => {
    const { terminalNames } = data;
    return terminalNames.find((t) => t.id === id)?.name;
  }, [data.terminalNames]);

  return (
    <Wrapper>
      <InnerContent>
        <TerminalRowCell>
          <div>{i18n('Settings.TerminalID')}</div>
          <b>{id}</b>
        </TerminalRowCell>

        <TerminalRowCell>
          {terminalName && (
            <>
              <div>{i18n('Settings.Terminal.Name')}</div>
              <b>{terminalName}</b>
            </>
          )}
        </TerminalRowCell>
        <TerminalRowCell>
          <AddButton {...button}>{i18n('Settings.Add')}</AddButton>
        </TerminalRowCell>
      </InnerContent>
    </Wrapper>
  );
};

export default AvailableTerminal;
